import { isoDateTime, z } from "../zod.js";

// Module 16 — Identity, Auth & Tenancy Core: the PR #5 global-auth subset
// (spec 16 §5).
//
// Deferred to later PRs (deliberately absent here — no stubs, D-026):
// - Tenancy-dependent: invites, members, roles, org, security-policy, tenant
//   picker, GET /v1/auth/session (grants), subdomain + CSRF middleware,
//   permission guard, tenant-table RLS — PR #6/#7.
// - Global-auth but tenancy-coupled or lower-priority, each landing with the
//   surface it needs: POST /v1/auth/password/change (shipped with PR #6) and
//   GET/PATCH /v1/auth/profile (shipped with P3-UX L1, D-430, in
//   admin-schemas.ts) and GET /v1/auth/events/own and POST /v1/auth/sessions/revoke_all (ship
//   with the office web area, PR #6); POST /v1/auth/totp/disable (needs the
//   cross-tenant require_totp_roles check → PR #6).
// - Domain events + transactional outbox (spec §6) — events-infra PR.

export const Email = z.string().trim().toLowerCase().check(z.email()).max(320);

export const TenantSummary = z
  .object({
    tenant_id: z.guid(),
    name: z.string(),
    subdomain: z.string(),
    role_codes: z.array(z.string()),
    /**
     * The clone-lineage root of each role in `role_codes` (spec 16 §3.9.1,
     * D-224): a seed role is its own root, and a tenant's clone of `sales`
     * reads `sales` here under whatever code the tenant gave it. Sorted and
     * de-duplicated. A client keys a role-shaped default (which screens a
     * salesperson works in, where a floor worker lands) on this list, the way
     * the server keys its own narrowings on it (D-586).
     */
    role_lineage_codes: z.array(z.string()),
    /**
     * The permission codes the caller's active memberships at this tenant
     * carry: the union the membership guard folds for a request (spec 16
     * §3.1.4), sorted. Scope is not part of it, so a code held at one
     * facility appears as held. A client decides which areas and controls to
     * offer from this list rather than from role codes, so a clone, or a
     * role whose permissions the tenant edited, is offered what it can
     * actually do (D-586). The server still checks every request.
     */
    permissions: z.array(z.string()),
    status: z.string(),
    /**
     * The tenant's HQ timezone (`tenants.default_timezone`, NOT NULL — so this
     * is required, never optional: an absent zone would let a client fall back
     * to the browser's forever and never say so).
     *
     * WHY IT IS ON THE SESSION FEED. Business dates are evaluated in this zone
     * server-side (§9.14): `POST /v1/quotes` prices at `tenantToday(now,
     * tenant.defaultTimezone)`. A client that derives a business date from the
     * BROWSER's calendar disagrees with the server for the hours where the two
     * dates differ — D-089's rule, and the §4.4 composer's pre-save price was
     * the last surface still doing it. Every authenticated client already reads
     * this feed for its role codes, so the zone costs no extra request.
     *
     * NOT a facility timezone. `Facility.timezone` is a different, per-facility
     * value; using it here would disagree with the save.
     */
    default_timezone: z.string().min(1).max(64),
  })
  .openapi("TenantSummary");

export const LoginRequest = z
  .object({
    email: Email,
    // Transport-level bounds only: real policy (10–128 after NFKC, breach
    // list, ≠ email local part) is enforced server-side on SET, and login
    // must not leak policy shape.
    password: z.string().min(1).max(1024),
  })
  .openapi("LoginRequest");

export const LoginResponse = z
  .object({
    step: z.enum(["done", "totp", "totp_enrollment_required"]),
    // Populated from active memberships once tenancy lands (PR #6); [] until
    // then and always [] for step='totp' (memberships resolve post-TOTP).
    tenants: z.array(TenantSummary),
  })
  .openapi("LoginResponse");

export const TotpVerifyRequest = z
  .object({
    code: z.string().regex(/^\d{6}$/, "6-digit TOTP code"),
  })
  .openapi("TotpVerifyRequest");

export const AuthCompleted = z
  .object({
    step: z.literal("done"),
    tenants: z.array(TenantSummary),
  })
  .openapi("AuthCompleted");

export const ResetPasswordResponse = z
  .object({
    // 'totp' when the account has TOTP enrolled: the emailed link proves
    // mailbox control, never the second factor — the fresh session is pending
    // and only the TOTP allowlist responds until the challenge passes
    // (spec 16 §3.4.4).
    step: z.enum(["done", "totp"]),
    tenants: z.array(TenantSummary),
  })
  .openapi("ResetPasswordResponse");

export const RecoveryVerifyRequest = z
  .object({
    // XXXXX-XXXXX crockford base32; dashes/case are normalized server-side.
    code: z.string().min(10).max(12),
  })
  .openapi("RecoveryVerifyRequest");

export const RecoveryVerifyResponse = z
  .object({
    step: z.literal("done"),
    tenants: z.array(TenantSummary),
    remaining_codes: z.number().int().min(0),
    // true when remaining_codes ≤ 3 — the SPA prompts regeneration; the
    // "running low" SES notice fires at ≤ 2 (spec 16 §3.2.8).
    regeneration_recommended: z.boolean(),
  })
  .openapi("RecoveryVerifyResponse");

export const SessionItem = z
  .object({
    id: z.guid(),
    device_label: z.string().nullable(),
    ip: z.string().nullable(),
    created_at: isoDateTime(),
    last_seen_at: isoDateTime(),
    current: z.boolean(),
  })
  .openapi("SessionItem");

export const SessionListResponse = z
  .object({
    items: z.array(SessionItem),
  })
  .openapi("SessionListResponse");

export const ForgotPasswordRequest = z
  .object({
    email: Email,
  })
  .openapi("ForgotPasswordRequest");

export const ResetPasswordRequest = z
  .object({
    token: z.string().min(16).max(256),
    new_password: z.string().min(1).max(1024),
  })
  .openapi("ResetPasswordRequest");

export const TotpEnrollRequest = z
  .object({
    // Fresh password re-entry — session alone is not enough to change auth
    // factors (spec 16 §3.6.1).
    password: z.string().min(1).max(1024),
  })
  .openapi("TotpEnrollRequest");

export const TotpEnrollResponse = z
  .object({
    otpauth_uri: z.string(),
    secret_base32: z.string(),
    qr_svg: z.string(),
  })
  .openapi("TotpEnrollResponse");

export const TotpActivateRequest = z
  .object({
    code: z.string().regex(/^\d{6}$/, "6-digit TOTP code"),
  })
  .openapi("TotpActivateRequest");

export const RecoveryCode = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}$/);

export const RecoveryCodesResponse = z
  .object({
    // Shown exactly once; only sha256 hashes are stored (spec 16 §2.14).
    recovery_codes: z.array(RecoveryCode).length(10),
  })
  .openapi("RecoveryCodesResponse");

export const RegenerateRecoveryCodesRequest = z
  .object({
    password: z.string().min(1).max(1024),
    totp_code: z.string().regex(/^\d{6}$/),
  })
  .openapi("RegenerateRecoveryCodesRequest");

/**
 * Stable UPPER_SNAKE problem `code` values this module returns (spec 16 §5;
 * RFC 9457 envelope in common/problem.ts). Servers must not invent codes
 * outside their module's list.
 */
export const identityProblemCodes = [
  "INVALID_CREDENTIALS",
  "ACCOUNT_LOCKED",
  "TOO_MANY_ATTEMPTS",
  "TOTP_REQUIRED",
  "TOTP_NOT_PENDING",
  "INVALID_TOTP_CODE",
  "TOTP_NOT_ENABLED",
  "NO_PENDING_ENROLLMENT",
  "INVALID_OR_EXPIRED_TOKEN",
  "PASSWORD_POLICY",
  "NOT_AUTHENTICATED",
  "NOT_FOUND",
  "VALIDATION_FAILED",
] as const;

export type IdentityProblemCode = (typeof identityProblemCodes)[number];
